import type { JSX } from 'react';
import { GitBranch, LayoutDashboard } from 'lucide-react';

import { useRepositories } from '~/hooks/useRepositories';
import { useUIStore } from '~/stores/uiStore';

import type { Repository } from '~/types';

export function RepositoriesSidebar({ width }: { width: number }): JSX.Element {
  const { data: repositories, isError, isLoading } = useRepositories();
  const { selectedRepositoryId, setSelectedRepositoryId } = useUIStore();

  return (
    <aside
      style={{ width }}
      className="shrink-0 border-r border-default flex flex-col bg-surface-elevated border-t border-white/20"
    >
      <button
        type="button"
        // bg-[#0f172a]/text-white are intentionally theme-invariant, not an
        // overlooked literal: the selected row must contrast against the
        // sidebar's own bg-surface-elevated, which is white in light mode —
        // the same white as the theme-adaptive bg-sidebar-bg token would
        // resolve to, which would make the "selected" state invisible.
        // Always-dark-navy guarantees visible contrast in both themes.
        className={`w-full px-4 py-2.5 flex items-center gap-3 text-left border-b border-default transition-colors ${
          selectedRepositoryId === null
            ? 'bg-[#0f172a] text-white border-l-3 border-l-primary-400'
            : 'hover:bg-surface text-muted'
        }`}
        onClick={() => setSelectedRepositoryId(null)}
      >
        <LayoutDashboard
          className={`w-4 h-4 flex-shrink-0 ${selectedRepositoryId === null ? 'text-primary-400' : ''}`}
        />
        <span className="text-sm font-medium">Dashboard</span>
      </button>

      <div className="p-4 border-b border-default flex items-center justify-between">
        <span className="text-sm font-semibold text-foreground">
          Repositories {repositories ? `(${repositories.length})` : ''}
        </span>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading && <div className="p-4 text-sm text-muted">Loading repositories...</div>}
        {isError && <div className="p-4 text-sm text-error">Failed to load repositories</div>}
        {!isLoading && !isError && repositories?.length === 0 && (
          <div className="p-4 text-sm text-muted">
            No repositories registered yet. Use the <code>repo_init</code> MCP tool to register one.
          </div>
        )}
        {repositories?.map((repo: Repository) => (
          <button
            key={repo.id}
            type="button"
            // bg-[#0f172a] here is the same intentional theme-invariant
            // choice as the Dashboard row above — see its comment.
            className={`w-full px-4 py-3 flex items-center gap-3 text-left border-b border-default transition-colors ${
              selectedRepositoryId === repo.id
                ? 'bg-[#0f172a] text-white border-l-3 border-l-primary-400'
                : 'hover:bg-surface text-foreground'
            }`}
            onClick={() => setSelectedRepositoryId(repo.id)}
          >
            <GitBranch
              className={`w-4 h-4 flex-shrink-0 ${selectedRepositoryId === repo.id ? 'text-primary-400' : 'text-muted'}`}
            />
            <span className="flex-1 text-sm font-medium truncate">{repo.fullName}</span>
            {repo.projectCount !== undefined && repo.projectCount > 0 && (
              <span className="px-2 py-0.5 rounded-full text-xs bg-primary/10 text-primary border border-primary/20">
                {repo.projectCount}
              </span>
            )}
          </button>
        ))}
      </div>
    </aside>
  );
}
